import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2, Edit2, MessageSquare, ListChecks, FileText, X } from 'lucide-react'
import { adminApi } from '../../lib/api'
import type {
  ChecklistItem,
  ChecklistNoteContent,
  NoteModel,
  NotePrivacy,
  NoteType,
  TextNoteContent,
} from '../../types'
import { useToast } from '../ui/ToastProvider'

type FormState = {
  title: string
  noteType: NoteType
  privacy: NotePrivacy
  textContent: string
  checklistItems: ChecklistItem[]
}

const checklistBadgeClasses =
  'inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700'
const textBadgeClasses =
  'inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700'
const privacyBadgeClasses =
  'inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700'

const generateItemId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `item-${Math.random().toString(36).slice(2, 10)}`

const isChecklistContent = (
  content: NoteModel['content'],
): content is ChecklistNoteContent => {
  return Boolean(content) && typeof content === 'object' && Array.isArray((content as ChecklistNoteContent).items)
}

const parseChecklistItems = (rawItems: any[]): ChecklistItem[] =>
  rawItems
    .map((item: any, index: number) => ({
      id: item?.id || `item-${index}`,
      text: typeof item?.text === 'string' ? item.text : '',
      checked: Boolean(item?.checked),
      order: typeof item?.order === 'number' ? item.order : index,
    }))
    .sort((a, b) => a.order - b.order)

const parseNote = (note: any): NoteModel => {
  const rawType = note?.note_type ?? note?.noteType
  const noteType: NoteType = rawType === 'checklist' ? 'checklist' : 'text'

  let rawContent = note?.content ?? note?.content_json ?? note?.contentJson
  if (typeof rawContent === 'string') {
    try {
      rawContent = JSON.parse(rawContent)
    } catch (error) {
      rawContent =
        noteType === 'checklist'
          ? { items: [] }
          : { text: rawContent }
    }
  }

  if (!rawContent || typeof rawContent !== 'object') {
    rawContent = noteType === 'checklist' ? { items: [] } : { text: '' }
  }

  const content: TextNoteContent | ChecklistNoteContent =
    noteType === 'checklist'
      ? { items: parseChecklistItems(Array.isArray(rawContent?.items) ? rawContent.items : []) }
      : { text: typeof (rawContent as TextNoteContent)?.text === 'string' ? rawContent.text : '' }

  const privacy = (note?.privacy ?? 'trip_members') as NotePrivacy

  return {
    id: note?.id,
    title: note?.title ?? '',
    content,
    noteType,
    tripId: note?.trip_id ?? note?.tripId ?? null,
    eventId: note?.event_id ?? note?.eventId ?? null,
    privacy,
    createdBy: note?.created_by ?? note?.createdBy ?? '',
    createdAt: note?.created_at ?? note?.createdAt ?? Date.now(),
    updatedAt: note?.updated_at ?? note?.updatedAt ?? note?.created_at ?? note?.createdAt ?? Date.now(),
  }
}

const parseNotes = (data: any[]): NoteModel[] => (Array.isArray(data) ? data.map(parseNote) : [])

const createEmptyFormState = (): FormState => ({
  title: '',
  noteType: 'text',
  privacy: 'trip_members',
  textContent: '',
  checklistItems: [],
})

const formatDate = (value: string | number) => {
  if (!value) return ''
  if (typeof value === 'number') {
    const ms = value > 1000000000000 ? value : value * 1000
    return new Date(ms).toLocaleDateString()
  }
  const numeric = Number(value)
  if (!Number.isNaN(numeric) && value.trim() !== '') {
    const ms = numeric > 1000000000000 ? numeric : numeric * 1000
    return new Date(ms).toLocaleDateString()
  }
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? '' : new Date(parsed).toLocaleDateString()
}

const formatPrivacyLabel = (privacy: NotePrivacy) => {
  switch (privacy) {
    case 'public':
      return 'Public'
    case 'private':
      return 'Private'
    default:
      return 'Trip Members'
  }
}

interface NotesTabProps {
  tripId: string
  preloadedData?: any[]
}

export default function NotesTab({ tripId, preloadedData }: NotesTabProps) {
  const { showToast } = useToast()
  const [notes, setNotes] = useState<NoteModel[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingNote, setEditingNote] = useState<NoteModel | null>(null)
  const [formData, setFormData] = useState<FormState>(() => createEmptyFormState())

  const loadNotes = useCallback(async () => {
    if (!tripId) return
    setLoading(true)
    try {
      const response = await adminApi.getTripNotes(tripId)
      const rawData = response.data?.data || response.data || []
      setNotes(parseNotes(rawData))
    } catch (error) {
      console.error('Failed to load notes:', error)
      setNotes([])
    } finally {
      setLoading(false)
    }
  }, [tripId])

  useEffect(() => {
    if (Array.isArray(preloadedData)) {
      setNotes(parseNotes(preloadedData))
      setLoading(false)
    } else {
      loadNotes()
    }
  }, [preloadedData, loadNotes])

  const handleAddNoteClick = () => {
    if (showForm && !editingNote) {
      resetForm()
      return
    }
    setEditingNote(null)
    setFormData(createEmptyFormState())
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmedTitle = formData.title.trim()
    if (!trimmedTitle) {
      showToast('Title is required.', { type: 'error' })
      return
    }

    let content: TextNoteContent | ChecklistNoteContent
    if (formData.noteType === 'text') {
      const trimmedContent = formData.textContent.trim()
      if (!trimmedContent) {
        showToast('Content is required for text notes.', { type: 'error' })
        return
      }
      content = { text: trimmedContent }
    } else {
      const itemsWithText = formData.checklistItems
        .map((item) => ({
          ...item,
          text: item.text.trim(),
        }))
        .filter((item) => item.text.length > 0)

      if (itemsWithText.length === 0) {
        showToast('Add at least one checklist item.', { type: 'error' })
        return
      }

      const normalizedItems = itemsWithText.map((item, index) => ({
        ...item,
        order: index,
      }))

      content = { items: normalizedItems }
    }

    const payload = {
      title: trimmedTitle,
      note_type: formData.noteType,
      content,
      privacy: formData.privacy,
    }

    try {
      if (editingNote) {
        await adminApi.updateTripNote(tripId, editingNote.id, payload)
        showToast('Note updated successfully!', { type: 'success' })
      } else {
        await adminApi.createTripNote(tripId, payload)
        showToast('Note created successfully!', { type: 'success' })
      }
      await loadNotes()
      resetForm()
    } catch (error) {
      console.error('Failed to save note:', error)
      showToast('Failed to save note. Please try again.', { type: 'error' })
    }
  }

  const handleDelete = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) return
    try {
      await adminApi.deleteTripNote(tripId, noteId)
      await loadNotes()
    } catch (error) {
      console.error('Failed to delete note:', error)
      showToast('Failed to delete note. Please try again.', { type: 'error' })
    }
  }

  const handleEdit = (note: NoteModel) => {
    setEditingNote(note)
    if (note.noteType === 'checklist' && isChecklistContent(note.content)) {
      setFormData({
        title: note.title ?? '',
        noteType: 'checklist',
        privacy: note.privacy ?? 'trip_members',
        textContent: '',
        checklistItems: parseChecklistItems(note.content.items),
      })
    } else {
      setFormData({
        title: note.title ?? '',
        noteType: 'text',
        privacy: note.privacy ?? 'trip_members',
        textContent: (note.content as TextNoteContent)?.text ?? '',
        checklistItems: [],
      })
    }
    setShowForm(true)
  }

  const handleChecklistToggle = async (noteId: string, itemId: string, checked: boolean) => {
    const previousNotes = notes
    setNotes((prevNotes) =>
      prevNotes.map((note) => {
        if (note.id !== noteId || note.noteType !== 'checklist' || !isChecklistContent(note.content)) return note
        return {
          ...note,
          content: {
            items: note.content.items.map((item) =>
              item.id === itemId ? { ...item, checked } : item,
            ),
          },
        }
      }),
    )

    try {
      await adminApi.updateTripNoteChecklistItem(tripId, noteId, { item_id: itemId, checked })
    } catch (error) {
      console.error('Failed to update checklist item:', error)
      setNotes(previousNotes)
      showToast('Failed to update checklist item. Please try again.', { type: 'error' })
    }
  }

  const handleNoteTypeChange = (nextType: NoteType) => {
    setFormData((prev) => {
      if (nextType === 'checklist' && prev.checklistItems.length === 0) {
        return {
          ...prev,
          noteType: nextType,
          checklistItems: [
            { id: generateItemId(), text: '', checked: false, order: 0 },
          ],
        }
      }
      if (nextType === 'text') {
        return {
          ...prev,
          noteType: nextType,
          textContent: prev.textContent,
        }
      }
      return { ...prev, noteType: nextType }
    })
  }

  const handleAddChecklistItem = () => {
    setFormData((prev) => ({
      ...prev,
      checklistItems: [
        ...prev.checklistItems,
        { id: generateItemId(), text: '', checked: false, order: prev.checklistItems.length },
      ],
    }))
  }

  const handleChecklistItemTextChange = (itemId: string, text: string) => {
    setFormData((prev) => ({
      ...prev,
      checklistItems: prev.checklistItems.map((item) =>
        item.id === itemId ? { ...item, text } : item,
      ),
    }))
  }

  const handleChecklistItemCheckedChange = (itemId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      checklistItems: prev.checklistItems.map((item) =>
        item.id === itemId ? { ...item, checked } : item,
      ),
    }))
  }

  const handleRemoveChecklistItem = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      checklistItems: prev.checklistItems
        .filter((item) => item.id !== itemId)
        .map((item, index) => ({ ...item, order: index })),
    }))
  }

  const resetForm = () => {
    setShowForm(false)
    setEditingNote(null)
    setFormData(createEmptyFormState())
  }

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading notes...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Trip Notes</h3>
          <p className="text-sm text-gray-600 mt-1">Add text notes or build interactive checklists for the trip</p>
        </div>
        <button
          onClick={handleAddNoteClick}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {showForm && !editingNote ? 'Close' : 'Add Note'}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-4">
            {editingNote ? 'Edit Note' : 'New Note'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  className="input w-full"
                  placeholder="Note title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Note Type *
                </label>
                <select
                  value={formData.noteType}
                  onChange={(e) => handleNoteTypeChange(e.target.value as NoteType)}
                  className="input w-full"
                >
                  <option value="text">Text note</option>
                  <option value="checklist">Checklist</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Privacy *
              </label>
              <select
                value={formData.privacy}
                onChange={(e) => setFormData((prev) => ({ ...prev, privacy: e.target.value as NotePrivacy }))}
                className="input w-full"
              >
                <option value="trip_members">Trip members</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>

            {formData.noteType === 'text' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content *
                </label>
                <textarea
                  value={formData.textContent}
                  onChange={(e) => setFormData((prev) => ({ ...prev, textContent: e.target.value }))}
                  className="input w-full min-h-[200px]"
                  placeholder="Write your note here..."
                  required
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    Checklist Items *
                  </label>
                  <button
                    type="button"
                    onClick={handleAddChecklistItem}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Add item
                  </button>
                </div>
                <div className="space-y-3">
                  {formData.checklistItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 rounded border border-gray-200 bg-white p-3"
                    >
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={item.checked}
                        onChange={(e) => handleChecklistItemCheckedChange(item.id, e.target.checked)}
                      />
                      <input
                        type="text"
                        value={item.text}
                        onChange={(e) => handleChecklistItemTextChange(item.id, e.target.value)}
                        className="input flex-1"
                        placeholder="Checklist item description"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveChecklistItem(item.id)}
                        className="p-1 text-gray-500 hover:text-gray-700"
                        title="Remove item"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={resetForm}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
              >
                {editingNote ? 'Update Note' : 'Create Note'}
              </button>
            </div>
          </form>
        </div>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No notes yet</p>
          <p className="text-sm text-gray-500 mt-1">Add notes or checklists to keep track of important information</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notes.map((note) => {
            const isChecklist = note.noteType === 'checklist' && isChecklistContent(note.content)
            const checklistItems = isChecklist ? parseChecklistItems((note.content as ChecklistNoteContent).items) : []
            const noteTitle = note.title?.trim() ? note.title : 'Untitled note'

            return (
              <div
                key={note.id}
                className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {isChecklist ? (
                        <ListChecks className="h-4 w-4 text-blue-600" />
                      ) : (
                        <FileText className="h-4 w-4 text-purple-600" />
                      )}
                      <h4 className="font-semibold text-gray-900">{noteTitle}</h4>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className={isChecklist ? checklistBadgeClasses : textBadgeClasses}>
                        {isChecklist ? 'Checklist' : 'Text note'}
                      </span>
                      <span className={privacyBadgeClasses}>
                        {formatPrivacyLabel(note.privacy)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleEdit(note)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(note.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {isChecklist ? (
                  <ul className="space-y-2 mb-3">
                    {checklistItems.map((item) => (
                      <li key={item.id} className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          checked={item.checked}
                          onChange={(e) => handleChecklistToggle(note.id, item.id, e.target.checked)}
                        />
                        <span
                          className={`flex-1 text-sm ${
                            item.checked ? 'text-gray-500 line-through' : 'text-gray-800'
                          }`}
                        >
                          {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words mb-3">
                    {(note.content as TextNoteContent)?.text}
                  </p>
                )}

                <div className="text-xs text-gray-500 border-t pt-2">
                  <div className="flex justify-between">
                    <span>{`Created: ${formatDate(note.createdAt)}`}</span>
                    {note.updatedAt !== note.createdAt && formatDate(note.updatedAt) && (
                      <span>{`Updated: ${formatDate(note.updatedAt)}`}</span>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
